import type { Healthcard, Patient, Provider, User } from 'interfaces';
import { organizationRefs, roleRefs, siteRefs } from './organizations';
import {
  FixtureRecord,
  normalizeHealthcard,
  normalizePatient,
  normalizeProvider,
  normalizeUser,
} from './shared';

const makeUserSeed = (
  user: Pick<
    User,
    | 'id'
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'phone'
    | 'status'
    | 'cognitoID'
    | 'organization'
    | 'organizationID'
    | 'role'
    | 'roleID'
  > &
    Partial<User>
): User => ({
  providers: [],
  providerID: null,
  ...user,
});

const providerDefaults: Omit<
  Provider,
  | 'id'
  | 'firstName'
  | 'lastName'
  | 'specialtyCode'
  | 'specialtyStartDate'
  | 'license'
  | 'userID'
  | 'sites'
  | 'siteID'
  | 'user'
> = {
  email: null,
  contactNote: '',
  province: 'MB',
  title: null,
  licenses: [],
  specialty: null,
  groups: [],
  createdAt: '2024-01-01T00:00:00.000Z',
  updatedAt: null,
};

const makeProviderSeed = (
  provider: Pick<
    Provider,
    | 'id'
    | 'firstName'
    | 'lastName'
    | 'specialtyCode'
    | 'specialtyStartDate'
    | 'license'
    | 'userID'
    | 'sites'
    | 'siteID'
    | 'user'
  > &
    Partial<Provider>
): Provider => ({
  ...providerDefaults,
  ...provider,
});

const patientDefaults: Omit<
  Patient,
  | 'id'
  | 'firstName'
  | 'lastName'
  | 'dateOfBirth'
  | 'address'
  | 'city'
  | 'country'
  | 'postalCode'
  | 'sex'
  | 'maritalStatus'
  | 'province'
  | 'organizationID'
  | 'organization'
> = {
  email: null,
  phone: null,
  searchVector: null,
  healthcards: [],
  healthcardID: null,
};

const makePatientSeed = (
  patient: Pick<
    Patient,
    | 'id'
    | 'firstName'
    | 'lastName'
    | 'dateOfBirth'
    | 'address'
    | 'city'
    | 'country'
    | 'postalCode'
    | 'sex'
    | 'maritalStatus'
    | 'province'
    | 'organizationID'
    | 'organization'
  > &
    Partial<Patient>
): Patient => ({
  ...patientDefaults,
  ...patient,
});

const makeHealthcardSeed = (
  healthcard: Pick<
    Healthcard,
    'id' | 'number' | 'registration' | 'province' | 'patient' | 'patientID'
  > &
    Partial<Healthcard>
): Healthcard => ({
  ...healthcard,
});

export const userRefs = {
  kyleConnor: makeUserSeed({
    firstName: 'Kyle',
    lastName: 'Connor',
    email: 'a_j_don@yahoo.ca',
    status: 'active',
    phone: '+12048917104',
    id: 'c9fca33e-542a-46c8-bcdd-c72aba7e2740',
    cognitoID: 'cc1d85c8-20b1-701e-272e-eba22237bbd6',
    organization: organizationRefs.winnipegJets,
    organizationID: organizationRefs.winnipegJets.id,
    role: roleRefs.winnipegJetsAdmin,
    roleID: roleRefs.winnipegJetsAdmin.id,
  }),
  adamLowryUser: makeUserSeed({
    firstName: 'Adam',
    lastName: 'Lowry',
    email: 'adamqdoctest@gmail.com',
    status: 'active',
    phone: '+12048917104',
    id: '53569941-3c6c-4c2f-acb7-c54259406f44',
    cognitoID: '3cad1508-9041-70bd-e3bc-735c1f1c5f77',
    organization: organizationRefs.winnipegJets,
    organizationID: organizationRefs.winnipegJets.id,
    role: roleRefs.winnipegJetsAdmin,
    roleID: roleRefs.winnipegJetsAdmin.id,
  }),
  fakeIntegrationUser: makeUserSeed({
    firstName: 'Fake',
    lastName: 'User',
    email: 'qbillingtestuser@gmail.com',
    status: 'active',
    phone: '+12048917104',
    id: 'ead48e53-8161-489b-8f85-f7457a031a44',
    cognitoID: '5ccd5508-4071-700d-4c4f-2f04cb6185ae',
    organization: organizationRefs.integrationTest,
    organizationID: organizationRefs.integrationTest.id,
    role: roleRefs.integrationTestUser,
    roleID: roleRefs.integrationTestUser.id,
  }),
  normanSilverUser: makeUserSeed({
    firstName: 'Norman',
    lastName: 'Silver',
    email: 'jwatts@qdoc.ca',
    status: 'active',
    phone: '+12045555555',
    id: 'e1797cc4-4e5f-4879-9712-f99db242f9dd',
    cognitoID: 'norman-silver-cognito',
    organization: organizationRefs.winnipegJets,
    organizationID: organizationRefs.winnipegJets.id,
    role: roleRefs.winnipegJetsUser,
    roleID: roleRefs.winnipegJetsUser.id,
  }),
  lebronJamesUser: makeUserSeed({
    firstName: 'Lebron',
    lastName: 'James',
    email: 'blungal.patient@qdoc.ca',
    status: 'active',
    phone: '+12045555555',
    id: '016a5ccd-1839-4124-80c5-6730439cbab7',
    cognitoID: 'lebron-james-cognito',
    organization: organizationRefs.winnipegJets,
    organizationID: organizationRefs.winnipegJets.id,
    role: roleRefs.winnipegJetsUser,
    roleID: roleRefs.winnipegJetsUser.id,
  }),
  weezerUser: makeUserSeed({
    firstName: 'Weezer',
    lastName: 'RockMusic',
    email: 'weezer-rockmusic@example.com',
    status: 'active',
    phone: '+12045555555',
    id: 'e73e9bf0-768f-4b48-9eaf-b2de13194d96',
    cognitoID: 'weezer-rockmusic-cognito',
    organization: organizationRefs.winnipegJets,
    organizationID: organizationRefs.winnipegJets.id,
    role: roleRefs.winnipegJetsUser,
    roleID: roleRefs.winnipegJetsUser.id,
  }),
  adamDonenUser: makeUserSeed({
    firstName: 'Adam',
    lastName: 'Donen',
    email: 'adampqdoc@gmail.com',
    status: 'active',
    phone: '+12045555555',
    id: '3356a13b-2339-4356-aeae-c9131b6dbbaf',
    cognitoID: 'adam-donen-cognito',
    organization: organizationRefs.safeway,
    organizationID: organizationRefs.safeway.id,
    role: roleRefs.orgAdmin,
    roleID: roleRefs.orgAdmin.id,
  }),
} satisfies Record<string, User>;

export const providerRefs = {
  normanSilver: makeProviderSeed({
    id: '26c02071-92d9-4e36-8153-2496888972f9',
    firstName: 'NORMAN A',
    lastName: 'SILVER',
    specialtyCode: '02',
    specialtyStartDate: '2002-06-05',
    license: '09994',
    email: 'jwatts@qdoc.ca',
    userID: 'e1797cc4-4e5f-4879-9712-f99db242f9dd',
    sites: [siteRefs.canadaLifeCentre],
    siteID: siteRefs.canadaLifeCentre.id,
    user: userRefs.normanSilverUser,
  }),
  brettsNurse: makeProviderSeed({
    id: '2903eb4e-f1b1-43aa-bb93-ab1248e71691',
    firstName: 'Bretts',
    lastName: 'Nurse',
    specialtyCode: '111',
    specialtyStartDate: '2024-01-01',
    license: '11233',
    title: 'Np.',
    email: 'brettlungal@gmail.com',
    userID: null,
    sites: [siteRefs.canadaLifeCentre],
    siteID: siteRefs.canadaLifeCentre.id,
    user: null,
  }),
  lebronJames: makeProviderSeed({
    id: 'a2ed2c88-3474-4601-b146-679081201a7a',
    firstName: 'Lebron',
    lastName: 'James',
    specialtyCode: '00000',
    specialtyStartDate: '2024-01-01',
    license: '00018',
    email: 'blungal.patient@qdoc.ca',
    userID: '016a5ccd-1839-4124-80c5-6730439cbab7',
    sites: [siteRefs.canadaLifeCentre],
    siteID: siteRefs.canadaLifeCentre.id,
    user: userRefs.lebronJamesUser,
  }),
  weezerRockMusic: makeProviderSeed({
    id: 'a61cf479-ba8a-4459-b99c-d67c31e206be',
    firstName: 'Weezer',
    lastName: 'RockMusic',
    specialtyCode: '111',
    specialtyStartDate: '2024-01-01',
    license: '00019',
    email: null,
    userID: 'e73e9bf0-768f-4b48-9eaf-b2de13194d96',
    sites: [siteRefs.canadaLifeCentre],
    siteID: siteRefs.canadaLifeCentre.id,
    user: userRefs.weezerUser,
  }),
  adamDonen: makeProviderSeed({
    id: '1e9f3433-f864-4e52-9835-7f2305178968',
    firstName: 'Adam',
    lastName: 'Donen',
    specialtyCode: '111',
    specialtyStartDate: '2024-01-01',
    license: '00069',
    email: 'adampqdoc@gmail.com',
    userID: '3356a13b-2339-4356-aeae-c9131b6dbbaf',
    sites: [siteRefs.canadaLifeCentre],
    siteID: siteRefs.canadaLifeCentre.id,
    user: userRefs.adamDonenUser,
  }),
  stephCurry: makeProviderSeed({
    id: '98c3684d-ce28-4d14-88e4-a9bfe1695be9',
    firstName: 'Steph',
    lastName: 'Curry',
    specialtyCode: '00000',
    specialtyStartDate: '2024-10-01',
    license: '00015',
    email: 'stephcurry@gmail.com',
    userID: null,
    sites: [siteRefs.canadaLifeCentre],
    siteID: siteRefs.canadaLifeCentre.id,
    user: null,
  }),
} satisfies Record<string, Provider>;

export const patientRefs = {
  adamLowry: makePatientSeed({
    id: 'a6afca80-774b-4045-9655-1549af9943e7',
    firstName: 'Adam',
    lastName: 'Lowry',
    dateOfBirth: '2000-01-01',
    address: '123 Fake St',
    city: 'Winnipeg',
    country: 'Canada',
    postalCode: 'R1R1R1',
    sex: 'M',
    maritalStatus: 'Single',
    province: 'MB',
    organizationID: organizationRefs.safeway.id,
    organization: organizationRefs.safeway,
  }),
  ronWeasley: makePatientSeed({
    id: 'e2fc8fb1-ec65-4f36-9737-c006de7601ec',
    firstName: 'Ron',
    lastName: 'Weasley',
    dateOfBirth: '1996-06-25',
    address: '4 Patient Way',
    city: 'Winnipeg',
    country: 'Canada',
    postalCode: 'T4B4B6',
    sex: 'Male',
    maritalStatus: 'S',
    province: 'MB',
    organizationID: organizationRefs.safeway.id,
    organization: organizationRefs.safeway,
  }),
  kyleConnor: makePatientSeed({
    id: 'edadf351-243a-43e5-a4f1-9cda9aa19c81',
    firstName: 'Kyle',
    lastName: 'Connor',
    dateOfBirth: '2000-01-01',
    address: '123 Fake St',
    city: 'Winnipeg',
    country: 'Canada',
    postalCode: 'R1R1R1',
    sex: 'M',
    maritalStatus: 'Single',
    province: 'MB',
    organizationID: organizationRefs.safeway.id,
    organization: organizationRefs.safeway,
  }),
  charlesHart: makePatientSeed({
    id: '63ae2304-d05d-43eb-94fa-374453cc5c7b',
    firstName: 'Charles',
    lastName: 'Hart',
    dateOfBirth: '2019-03-12',
    address: '1772 Danielle Parkways',
    city: 'Winnipeg',
    country: 'Canada',
    postalCode: 'I1K 2R3',
    sex: 'F',
    maritalStatus: 'Single',
    province: 'MB',
    organizationID: organizationRefs.safeway.id,
    organization: organizationRefs.safeway,
  }),
  codyAnthony: makePatientSeed({
    id: '3c9f80ae-61f3-4bee-a820-2773c80e7020',
    firstName: 'Cody',
    lastName: 'Anthony',
    dateOfBirth: '1924-12-27',
    address: '703 Ashley Court Suite 438',
    city: 'Winnipeg',
    country: 'Canada',
    postalCode: 'W6J 3P2',
    sex: 'F',
    maritalStatus: 'Married',
    province: 'MB',
    organizationID: organizationRefs.safeway.id,
    organization: organizationRefs.safeway,
  }),
  charlesSmith: makePatientSeed({
    id: '501f607d-60a7-43ac-a924-fed61a354fe5',
    firstName: 'Charles',
    lastName: 'Smith',
    dateOfBirth: '1979-05-18',
    address: '596 Vega Lock',
    city: 'Winnipeg',
    country: 'Canada',
    postalCode: 'P2D 7P7',
    sex: 'M',
    maritalStatus: 'Married',
    province: 'MB',
    organizationID: organizationRefs.safeway.id,
    organization: organizationRefs.safeway,
  }),
} satisfies Record<string, Patient>;

export const healthcardRefs = {
  adamLowryPrimary: makeHealthcardSeed({
    id: '6adc5d16-dd0e-4ecd-b4a9-e77edc3b71be',
    number: '746145900',
    registration: '161555',
    province: 'MB',
    patient: patientRefs.adamLowry,
    patientID: patientRefs.adamLowry.id,
  }),
  ronWeasleyPrimary: makeHealthcardSeed({
    id: '7a21e975-63fc-400d-96fc-32f203ed25d8',
    number: '240092396',
    registration: '452000',
    province: 'MB',
    patient: patientRefs.ronWeasley,
    patientID: patientRefs.ronWeasley.id,
  }),
  kyleConnorPrimary: makeHealthcardSeed({
    id: '4a380382-754b-4de6-9139-24c5f02c27d2',
    number: '884233070',
    registration: '558097',
    province: 'MB',
    patient: patientRefs.kyleConnor,
    patientID: patientRefs.kyleConnor.id,
  }),
  charlesHartPrimary: makeHealthcardSeed({
    id: '29ee2072-e7a2-4bff-90c6-f82ca7196cf9',
    number: '164673794',
    registration: '354053',
    province: 'MB',
    patient: patientRefs.charlesHart,
    patientID: patientRefs.charlesHart.id,
  }),
  codyAnthonyPrimary: makeHealthcardSeed({
    id: 'e80b8135-0106-4443-9437-b7de693b210e',
    number: '389655712',
    registration: '391883',
    province: 'MB',
    patient: patientRefs.codyAnthony,
    patientID: patientRefs.codyAnthony.id,
  }),
  charlesSmithPrimary: makeHealthcardSeed({
    id: '5dda0597-f0fa-4d7e-84df-d417594eb8a7',
    number: '271851964',
    registration: '766381',
    province: 'MB',
    patient: patientRefs.charlesSmith,
    patientID: patientRefs.charlesSmith.id,
  }),
  adamLowrySecondary: makeHealthcardSeed({
    id: '065fea23-a2a0-4e60-b1ff-a91d156720c2',
    number: '392968610',
    registration: '213042',
    province: 'MB',
    patient: patientRefs.adamLowry,
    patientID: patientRefs.adamLowry.id,
  }),
} satisfies Record<string, Healthcard>;

export const canonicalProviders: Provider[] = [
  providerRefs.normanSilver,
  providerRefs.brettsNurse,
  providerRefs.lebronJames,
  providerRefs.weezerRockMusic,
  providerRefs.adamDonen,
  providerRefs.stephCurry,
].map(normalizeProvider);

const providersByUserID = canonicalProviders.reduce((map, provider) => {
  if (!provider.userID) {
    return map;
  }

  const existing = map.get(provider.userID) ?? [];
  map.set(provider.userID, [...existing, provider]);
  return map;
}, new Map<string, FixtureRecord[]>());

export const canonicalUsers: User[] = [
  userRefs.kyleConnor,
  userRefs.adamLowryUser,
  userRefs.fakeIntegrationUser,
  userRefs.normanSilverUser,
  userRefs.lebronJamesUser,
  userRefs.weezerUser,
  userRefs.adamDonenUser,
].map((user) => normalizeUser(user, providersByUserID));

export const canonicalHealthcards: Healthcard[] = [
  healthcardRefs.adamLowryPrimary,
  healthcardRefs.ronWeasleyPrimary,
  healthcardRefs.kyleConnorPrimary,
  healthcardRefs.charlesHartPrimary,
  healthcardRefs.codyAnthonyPrimary,
  healthcardRefs.charlesSmithPrimary,
  healthcardRefs.adamLowrySecondary,
].map(normalizeHealthcard);

export const canonicalPatients: Patient[] = [
  patientRefs.adamLowry,
  patientRefs.ronWeasley,
  patientRefs.kyleConnor,
  patientRefs.charlesHart,
  patientRefs.codyAnthony,
  patientRefs.charlesSmith,
].map((patient) =>
  normalizePatient(
    patient,
    canonicalHealthcards.filter(
      (healthcard) => healthcard.patientID === patient.id
    )
  )
);

export const canonicalProviderByID = new Map(
  canonicalProviders.map((provider) => [provider.id, provider])
);
export const canonicalPatientByID = new Map(
  canonicalPatients.map((patient) => [patient.id, patient])
);
export const canonicalHealthcardByID = new Map(
  canonicalHealthcards.map((healthcard) => [healthcard.id, healthcard])
);
